import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import AddEntity from "components/Entity/addMoreEntity";
import { US } from "constants/countryCodes";
import { useFormikContext } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import { useCreateEntityMutation } from "store/apis/group";
import { BackendError } from "types/utils/error";

export type NonUsEntitySelectProps = {
  placeholder: string;
  name: string;
  disabled: boolean;
  required: boolean;
  groupId: string;
  fieldKey: string;
};

export const NonUsEntitySelect = ({
  placeholder,
  name,
  fieldKey,
  disabled,
  required,
  groupId,
}: NonUsEntitySelectProps) => {
  const { setFieldValue, values } = useFormikContext<{
    [key: string]: string;
  }>();
  const selectedValue = values[fieldKey] || "";
  const { entities } = useCurrentGroupContext();
  const [createEntity, { isLoading: isCreating }] = useCreateEntityMutation();
  const addEntityModal = useModal();
  const { successToast, alertToast } = useToast();

  const addEntityData = async (groupData: {
    company_name: any;
    formation_type_id: any;
    country_id: any;
    state_id: any;
    entity_type: any;
  }) => {
    try {
      let payload = {
        company_name: groupData.company_name,
        formation_type_id: groupData.formation_type_id,
        country_id: groupData.country_id,
        state_id: groupData.state_id,
        entity_type: groupData.entity_type,
      };
      const entity = await createEntity({ groupId, payload }).unwrap();

      setFieldValue(name, entity.name);
      successToast({ message: "Subsidiary created successfully" });
      addEntityModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const nonUsEntitiesOptions = useMemo(
    () =>
      entities
        .filter((entity) => entity.country_code !== US)
        .map((entity) => ({ label: entity.name, value: entity.name })),
    [entities]
  );

  const selectedOption = nonUsEntitiesOptions.find(
    (option) => option.value === selectedValue
  );

  return (
    <>
      <Combobox
        label=""
        components={{
          ClearIndicator: () => null,
        }}
        withForm
        placeholder={placeholder}
        name={name}
        options={nonUsEntitiesOptions}
        value={selectedOption ? selectedOption : null}
        isDisabled={disabled}
        required={required}
        actions={
          <Button customType="link" type="button" onClick={addEntityModal.open}>
            Add new foreign subsidiary
          </Button>
        }
        isLoading={isCreating}
      />
      <AddEntity
        addEntityData={addEntityData}
        showAddEntity={addEntityModal.isOpen}
        setShowAddEntity={addEntityModal.close}
        isCreating={isCreating}
        modalTitle="Add Foreign Subsidiary"
        skipCountry={["US"]}
      />
    </>
  );
};
